// Enhanced status helper functions with date-based calculations

export const reservationStatuses = {
  completed: ['terminé', 'completed', 'finished', 'done', 'closed', 'completé'],
  active: ['active', 'confirmed', 'en cours', 'in progress', 'actif', 'confirmé'],
  pending: ['pending', 'waiting', 'en attente'],
  cancelled: ['cancelled', 'annulé', 'canceled']
};

// Car statuses meaning "available to rent". 'disponible' is the only value
// ever assigned by the app's own car form (client/src/pages/FormulaireVoiturePage.jsx);
// 'available'/'libre' are kept for defensive compatibility with older/external data.
export const availableCarStatuses = ['disponible', 'available', 'libre'];

export const isAvailableCar = (car) =>
availableCarStatuses.includes((car?.status || '').toLowerCase());

export const maintenanceStatuses = {
  historic: ['historic', 'completed', 'terminé', 'done', 'archived', 'historique', 'completé', 'record'],
  reminder: ['reminder', 'scheduled', 'planned', 'à faire', 'programmé', 'planifié', 'pending'],
  active: ['active', 'in progress', 'en cours']
};

// Calculate reservation status based on dates and times
export const calculateReservationStatus = (reservation) => {
  const status = reservation.status?.toLowerCase();

  // Respect manually set terminal statuses before date logic
  if (status === 'cancelled') return 'cancelled';
  if (reservationStatuses.completed.includes(status)) return 'completed';

  const now = new Date();

  // Parse start and end dates with times
  const startDateTime = parseDateTime(reservation.startDate, reservation.startTime);
  const endDateTime = parseDateTime(reservation.endDate, reservation.endTime);

  // If dates are invalid, return current status
  if (!startDateTime || !endDateTime) {
    return reservation.status || 'confirmed';
  }

  // Treat end-date as completed once that calendar day is reached (ignore time-of-day)
  const endDateOnly = (reservation.endDate || reservation.end_date || '').split('T')[0];
  const todayOnly = now.toISOString().split('T')[0];
  if (endDateOnly && endDateOnly <= todayOnly && now >= startDateTime) {
    return 'completed';
  }

  if (now < startDateTime) {
    return 'confirmed'; // Future reservation
  }

  // Within the rental window (started, not yet completed) — currently active,
  // regardless of what the raw DB status string still says (it may not have
  // been auto-flipped by the server yet). Trusting the raw status here was
  // the same "active reservations wrong" bug already fixed in Header.jsx.
  return 'active';
};

// Helper function to parse date and time
const parseDateTime = (dateString, timeString) => {
  if (!dateString) return null;

  try {
    const dateOnly = dateString.split('T')[0];
    const time = timeString && timeString !== 'null' ? timeString : '00:00:00';
    const dt = new Date(`${dateOnly}T${time}`);
    return isNaN(dt.getTime()) ? new Date(dateOnly) : dt;
  } catch (e) {
    return null;
  }
};

// Check if reservation is completed (for financial calculations)
export const isCompletedReservation = (reservation) => {
  const calculatedStatus = calculateReservationStatus(reservation);
  return calculatedStatus === 'completed';
};

// Check if reservation is active
export const isActiveReservation = (reservation) => {
  const calculatedStatus = calculateReservationStatus(reservation);
  return calculatedStatus === 'active';
};

// Check if reservation is confirmed
export const isConfirmedReservation = (reservation) => {
  const calculatedStatus = calculateReservationStatus(reservation);
  return calculatedStatus === 'confirmed';
};

// Check if maintenance is a completed record (actual expense)
export const isHistoricMaintenance = (maintenance) => {
  // For maintenance records, they are always historic (completed services)
  if (maintenance.service_date && maintenance.cost) {
    return true; // This is a completed maintenance record
  }

  // For reminders, check if they're completed
  const status = maintenance?.status?.toLowerCase();
  return maintenanceStatuses.historic.includes(status);
};

// Check if maintenance is a reminder (future expense)
export const isReminderMaintenance = (maintenance) => {
  // Maintenance reminders are always future expenses
  if (maintenance.reminder_type || maintenance.target_date || maintenance.target_mileage) {
    return true;
  }

  const status = maintenance?.status?.toLowerCase();
  return maintenanceStatuses.reminder.includes(status);
};

// Check if maintenance is active (in progress)
export const isActiveMaintenance = (maintenance) => {
  const status = maintenance?.status?.toLowerCase();
  return maintenanceStatuses.active.includes(status);
};

// Helper to check if maintenance item is a record (has cost and service_date)
export const isMaintenanceRecord = (maintenance) => {
  return !!(maintenance.service_date && maintenance.cost);
};

// Helper to check if maintenance item is a reminder (has target_date or target_mileage)
export const isMaintenanceReminder = (maintenance) => {
  return !!(maintenance.reminder_type || maintenance.target_date || maintenance.target_mileage);
};